import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function formatoPesos(value) {
    return '$ ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FormatoFechaSinHora(value) {
    const fecha = new Date(value);
    return format(fecha, 'dd-MMMM-yyyy', { locale: es }).toUpperCase();
}

function FormatoFechaConHora(value) {
    const fecha = new Date(value);
    return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

function calcularAnchoProporcional(base64, alturaFija) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const ancho = img.naturalWidth && img.naturalHeight
                ? (img.naturalWidth * alturaFija) / img.naturalHeight
                : 50;
            resolve(ancho);
        };
        img.onerror = () => resolve(50);
        img.src = base64;
    });
}

function detectarFormatoImagen(base64) {
    if (base64.startsWith('data:image/png'))  return 'PNG';
    if (base64.startsWith('data:image/jpeg')) return 'JPEG';
    if (base64.startsWith('data:image/jpg'))  return 'JPEG';
    if (base64.startsWith('data:image/gif'))  return 'GIF';
    if (base64.startsWith('data:image/webp')) return 'WEBP';
    return 'PNG';
}

const BalanzaPDF = async (ObjBalanza, ObjBalanzaTodos, objEmpresa, fechaI, fechaF, sucursal) => {
    return new Promise(async (resolve, reject) => {
        try {
            const doc = new jsPDF({ format: 'letter' });

            const color               = objEmpresa?.color || '#662e91';
            const logoBase64          = objEmpresa?.logo?.base64 || '';
            const fechaHoraGeneracion = FormatoFechaConHora(new Date());

            const margenIzq = 12;
            const margenDer = 12;
            const anchoUtil = 215 - margenIzq - margenDer; // ~191mm
            const xDer      = 215 - margenDer;

            // ── CABECERA ──────────────────────────────────────────────────
            let y = 14;

            // Logo
            if (logoBase64 !== '') {
                const alturaLogo = 20;
                const formatoImg = detectarFormatoImagen(logoBase64);
                const anchoLogo  = await calcularAnchoProporcional(logoBase64, alturaLogo);
                doc.addImage(logoBase64, formatoImg, margenIzq, y - 5, anchoLogo, alturaLogo);
            }

            // Título + datos empresa (derecha)
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Balanza de Comprobación', xDer, y, { align: 'right' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const suc = sucursal ? ' - ' + sucursal : '';
            doc.text((objEmpresa?.rfc || '') + ' | ' + (objEmpresa?.nombre || '') + suc, xDer, y += 6, { align: 'right' });

            const periodo = 'PERIODO DEL ' + FormatoFechaSinHora(fechaI) + ' AL ' + FormatoFechaSinHora(fechaF);
            doc.text(periodo, xDer, y += 5, { align: 'right' });

            y += 10;

            // ── TOTALES ───────────────────────────────────────────────────
            const cuentas       = ObjBalanzaTodos.filter(o => o.tipoCuenta === 'R');
            const sumDebeInicial  = cuentas.reduce((a, b) => a + b.debeInicial,  0);
            const sumHaberInicial = cuentas.reduce((a, b) => a + b.haberInicial, 0);
            const sumCargo        = cuentas.reduce((a, b) => a + b.cargo,        0);
            const sumAbono        = cuentas.reduce((a, b) => a + b.abono,        0);
            const sumDebeFinal    = cuentas.reduce((a, b) => a + b.debeFinal,    0);
            const sumHaberFinal   = cuentas.reduce((a, b) => a + b.haberFinal,   0);

            // ── TABLA DE BALANZA (autoTable) ──────────────────────────────
            doc.autoTable({
                startY: y,
                head: [[
                    { content: 'Cuenta',  styles: { halign: 'left'  } },
                    { content: 'Nombre',  styles: { halign: 'left'  } },
                    { content: 'Debe\n(Inicial)',  styles: { halign: 'right' } },
                    { content: 'Haber\n(Inicial)', styles: { halign: 'right' } },
                    { content: 'Cargos',  styles: { halign: 'right' } },
                    { content: 'Abonos',  styles: { halign: 'right' } },
                    { content: 'Debe\n(Final)',  styles: { halign: 'right' } },
                    { content: 'Haber\n(Final)', styles: { halign: 'right' } },
                ]],
                body: ObjBalanza.map(c => ([
                    { content: c.noCuenta     || '', styles: { halign: 'left'  } },
                    { content: c.nombreCuenta || '', styles: { halign: 'left'  } },
                    { content: formatoPesos(c.debeInicial),  styles: { halign: 'right' } },
                    { content: formatoPesos(c.haberInicial), styles: { halign: 'right' } },
                    { content: formatoPesos(c.cargo),        styles: { halign: 'right' } },
                    { content: formatoPesos(c.abono),        styles: { halign: 'right' } },
                    { content: formatoPesos(c.debeFinal),    styles: { halign: 'right' } },
                    { content: formatoPesos(c.haberFinal),   styles: { halign: 'right' } },
                ])),
                foot: [[
                    { content: 'TOTALES:', colSpan: 2, styles: { halign: 'left', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                    { content: formatoPesos(sumDebeInicial),  styles: { halign: 'right', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                    { content: formatoPesos(sumHaberInicial), styles: { halign: 'right', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                    { content: formatoPesos(sumCargo),        styles: { halign: 'right', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                    { content: formatoPesos(sumAbono),        styles: { halign: 'right', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                    { content: formatoPesos(sumDebeFinal),    styles: { halign: 'right', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                    { content: formatoPesos(sumHaberFinal),   styles: { halign: 'right', fontStyle: 'bold', fillColor: color, textColor: '#ffffff' } },
                ]],
                styles: {
                    fontSize: 6,
                    cellPadding: 2,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: color,
                    textColor: '#ffffff',
                    fontStyle: 'bold',
                },
                footStyles: {
                    fillColor: color,
                    textColor: '#ffffff',
                    fontStyle: 'bold',
                },
                alternateRowStyles: { fillColor: '#f4f4f4' },
                columnStyles: {
                    // 0: { cellWidth: 18 },  // Cuenta
                    1: { cellWidth: 50 },  // Nombre
                    // 2: { cellWidth: 24 },  // Debe Inicial
                    // 3: { cellWidth: 24 },  // Haber Inicial
                    // 4: { cellWidth: 20 },  // Cargos
                    // 5: { cellWidth: 20 },  // Abonos
                    // 6: { cellWidth: 15 },  // Debe Final
                    // 7: { cellWidth: 15 },  // Haber Final
                },
                margin: { left: margenIzq, right: margenDer },
                tableWidth: anchoUtil,
            });

            // ── PIE DE PÁGINA ─────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(150);

                const pageH = doc.internal.pageSize.getHeight();
                const pageW = doc.internal.pageSize.getWidth();

                doc.setDrawColor(color);
                doc.setLineWidth(0.3);
                doc.line(margenIzq, pageH - 16, pageW - margenDer, pageH - 16);

                doc.text('Generado: ' + fechaHoraGeneracion, margenIzq, pageH - 8, { align: 'left' });
                doc.text('Documento generado por ContaGo', pageW / 2, pageH - 8, { align: 'center' });
                doc.text('Página ' + i + ' de ' + totalPages, pageW - margenDer, pageH - 8, { align: 'right' });
            }

            const base64 = doc.output('datauristring');
            resolve(base64);
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
};

export { BalanzaPDF };